import { useState } from 'react';
import {
  APITagAttributes,
  ArdoqId,
  PersistedTagQuestion,
  SurveyQuestionType,
} from '@ardoq/api-types';
import {
  AsyncMultiselect,
  AsyncSelect,
  SelectOption,
  SelectOptionsOrGroups,
} from '@ardoq/select';
import { escapeRegExp } from 'lodash';
import { Locale, localeCompare } from '@ardoq/locale';
import { DEFAULT_ARDOQ_I18NLOCALE, type I18nLocale, t } from '@ardoq/i18n';
import { FetchTags, PartialAnswer, TagAnswer } from '../../types';
import { getReferenceOrTagQuestionUserFeedback } from '../getUserFeedback';
import { limitOptions } from '../QuestionOptions/questionOptionsOperations';
import { SurveyTagQuestionReadOnly } from './SurveyTagQuestionReadOnly';
import { getRangeValidator } from '../questionOperations';
import { isArdoqError } from '@ardoq/common-helpers';
import { QuestionContentWrapper } from '../questionsTypes';

const useTagOptions = ({
  surveyId,
  currentLocale,
  fetchTags,
}: {
  surveyId: ArdoqId;
  currentLocale: Locale;
  fetchTags: FetchTags;
}) => {
  const [cachedOptions, setCachedOptions] = useState<SelectOption<string>[]>();
  const [status, setStatus] = useState<
    'IDLE' | 'LOADING' | 'SUCCESS' | 'ERROR'
  >('IDLE');

  const loadOptions = async (
    inputValue: string
  ): Promise<SelectOptionsOrGroups<string>> => {
    if (status === 'LOADING') {
      return [];
    }

    if (cachedOptions) {
      const re = new RegExp(escapeRegExp(inputValue), 'i');
      return limitOptions(
        cachedOptions.filter(option => re.test(option.label))
      );
    }

    setStatus('LOADING');

    const tags = await fetchTags(surveyId);

    if (isArdoqError(tags)) {
      setStatus('ERROR');
      return [];
    }

    const options: SelectOption<string>[] = tags
      .map((tag: APITagAttributes) => ({
        value: tag._id,
        label: tag.name,
      }))
      .sort((a: SelectOption<string>, b: SelectOption<string>) =>
        localeCompare(a.label, b.label, currentLocale)
      )
      .filter(Boolean);

    setCachedOptions(options);
    setStatus('SUCCESS');

    return limitOptions(options);
  };
  return {
    loadOptions,
    options: cachedOptions,
    isLoading: status === 'LOADING',
    isError: status === 'ERROR',
  };
};

export type SurveyTagQuestionProps = {
  surveyId: ArdoqId;
  question: PersistedTagQuestion;
  answer: TagAnswer | undefined;
  value?: ArdoqId[];
  currentLocale: Locale;
  i18nLocale?: I18nLocale;
  hideReadOnlyLabel: boolean;
  QuestionContentWrapper: QuestionContentWrapper;
  questionLabel: string;
  highlightUnansweredPriorityQuestions: boolean;
  setAnswer: (args: PartialAnswer) => void;
  fetchTags: FetchTags;
};

export const SurveyTagQuestion = ({
  surveyId,
  question,
  answer,
  value,
  currentLocale,
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
  hideReadOnlyLabel,
  QuestionContentWrapper,
  questionLabel,
  highlightUnansweredPriorityQuestions,
  setAnswer,
  fetchTags,
}: SurveyTagQuestionProps) => {
  const questionId = question._id;
  const validators = question.validators;
  const readonly = question.readonly ?? false;
  const { options, loadOptions, isLoading, isError } = useTagOptions({
    surveyId,
    currentLocale,
    fetchTags,
  });

  if (readonly) {
    loadOptions('');
    const readonlyTags = options?.filter(option =>
      value?.includes(option.value)
    );

    return (
      <SurveyTagQuestionReadOnly
        question={question}
        answer={answer}
        readonlyTags={readonlyTags}
        hideReadOnlyLabel={hideReadOnlyLabel}
        i18nLocale={i18nLocale}
        QuestionContentWrapper={QuestionContentWrapper}
        highlightUnansweredPriorityQuestions={
          highlightUnansweredPriorityQuestions
        }
      />
    );
  }

  const rangeValidator = getRangeValidator(validators);
  const showSingleSelect = Boolean(
    rangeValidator && rangeValidator.max === 1 && value && value.length <= 1
  );

  const userFeedback = getReferenceOrTagQuestionUserFeedback({
    value,
    rangeValidator,
    questionType: SurveyQuestionType.TAG,
    i18nLocale,
  });

  const commonSelectProps = {
    options,
    loadOptions,
    tabSelectsValue: false,
    isClearable: true,
    isSearchable: true,
    defaultOptions: true,
    isLoading,
    errorMessage: userFeedback?.error,
    warningMessage: userFeedback?.warning,
    isDisabled: readonly,
    i18nLocale,
  };

  const selectedTag = value && value.length ? value[0] : undefined;

  return (
    <QuestionContentWrapper
      question={question}
      answer={answer}
      highlightUnansweredPriorityQuestions={
        highlightUnansweredPriorityQuestions
      }
      i18nLocale={i18nLocale}
    >
      {showSingleSelect ? (
        <AsyncSelect
          {...commonSelectProps}
          value={selectedTag}
          aria-label={questionLabel}
          onChange={selectedOption => {
            if (!selectedOption) {
              setAnswer({
                questionId,
                value: [],
              });
              return;
            }
            setAnswer({
              questionId,
              value: [selectedOption.value],
            });
          }}
        />
      ) : (
        <AsyncMultiselect
          {...commonSelectProps}
          value={value}
          onChange={selectedOptions => {
            if (!selectedOptions) return;
            setAnswer({
              questionId,
              value: selectedOptions.map(option => option.value),
            });
          }}
          aria-label={questionLabel}
          escapeClearsValue={false}
          closeMenuOnSelect={false}
          noOptionsMessage={
            isError
              ? () =>
                  t(
                    i18nLocale,
                    'surveysUi:options.errorLoadingTags',
                    'Error loading tags'
                  )
              : undefined
          }
        />
      )}
    </QuestionContentWrapper>
  );
};
